import { useMemo, useCallback } from 'react';
import moment from 'moment';
import { cloneDeep, find, forEach } from 'lodash';
import stringUtils from '../../../../util/stringUtils';

/**
 * @fileoverview Hook de lógica de negocio para procesamiento de campos personalizados del cliente
 * @module useCustomerDetailLogic
 * @category Forms Layer / Business Logic
 * @since 1.0.0
 * @version 2.3.0
 */

/**
 * @typedef {Object} CustomerField
 * @description Campo personalizado guardado del cliente desde la API
 * @property {string} id - ID del campo guardado
 * @property {string} customer_related_field_id - ID de la definición del campo personalizado
 * @property {string} data - Valor guardado del campo (string, 't'/'f' para boolean)
 * @property {Object} customer_related_field - Referencia a la definición del campo
 * @property {string} customer_related_field.field_type - Tipo del campo ('boolean', 'date', 'text', 'list')
 */

/**
 * @typedef {Object} CustomField
 * @description Definición de campo personalizado disponible
 * @property {string} id - ID único de la definición del campo
 * @property {string} name - Nombre descriptivo del campo
 * @property {'boolean'|'date'|'text'|'list'} field_type - Tipo del campo personalizado
 * @property {boolean} required - Si el campo es obligatorio
 * @property {Array<ListOption>} [list_options] - Opciones para campos tipo 'list'
 */

/**
 * @typedef {Object} ListOption
 * @description Opción individual para campos tipo lista
 * @property {string} key - Clave de la opción
 * @property {string} value - Valor a mostrar de la opción
 */

/**
 * @typedef {Object} ProcessedCustomField
 * @description Campo personalizado procesado con valor asignado
 * @property {string} id - ID de la definición del campo
 * @property {string} name - Nombre descriptivo del campo
 * @property {'boolean'|'date'|'text'|'list'} field_type - Tipo del campo
 * @property {boolean} required - Si el campo es obligatorio
 * @property {*} answer - Valor procesado según el tipo (boolean, Date, string, etc.)
 * @property {string|null} id_saved - ID del valor guardado si existe
 * @property {Array<{value: string, label: string}>} [list_options] - Opciones formateadas para listas
 */

/**
 * @typedef {Object} UseCustomerDetailLogicReturn
 * @description Objeto retornado por el hook con datos y funciones procesadas
 * @property {ProcessedCustomField[]} customFieldsGrouped - Campos procesados con valores asignados
 * @property {function(ProcessedCustomField): string} formatAnswer - Función para formatear respuestas
 */

/**
 * Hook de lógica de negocio para procesamiento de campos personalizados del cliente.
 * 
 * Implementa el patrón de **Forms Layer / Business Logic** con separación estricta de
 * responsabilidades, enfocándose en el procesamiento, transformación y formateo de 
 * datos de campos personalizados sin manejar estado UI ni llamadas API.
 * 
 * @function useCustomerDetailLogic
 * @category Hook - Forms Layer / Business Logic
 * 
 * @description
 * **Arquitectura Optimizada (v2.3.0):**
 * - ✅ **Business Logic**: Procesamiento y transformación de datos
 * - ✅ **Performance**: useMemo y useCallback para optimización completa
 * - ✅ **Type Processing**: Manejo especializado por tipo de campo
 * - ✅ **Validation Logic**: Sistema robusto de validación de campos requeridos
 * - ✅ **Data Transformation**: Combinación inteligente de definiciones con valores
 * 
 * **Responsabilidades:**
 * - Procesar custom fields combinando definiciones con valores guardados
 * - Formatear respuestas según tipo de campo (boolean, date, text, list)
 * - Transformar datos entre formatos API y UI
 * - Normalizar opciones de lista para componentes UI
 * 
 * **NO Maneja:**
 * - ❌ Llamadas a API o carga de datos → useCustomerDetailData
 * - ❌ Estado de UI (modales, colapso) → useCustomerDetailUI
 * - ❌ Gestión de eventos de usuario → Components
 * - ❌ Estado de formularios → useCustomerDetailForm
 * 
 * **Tipos de Campo Soportados:**
 * - `boolean`: true/false con formato "Sí"/"No"
 * - `date`: Fechas con validación y formato moment.js
 * - `text`: Strings sin procesamiento especial
 * - `list`: Arrays de opciones con transformación key/value → value/label
 * 
 * @param {CustomerField[]} customerFields - Array de campos guardados desde la API
 * @param {CustomField[]} customFields - Array de definiciones de campos personalizados
 * 
 * @returns {UseCustomerDetailLogicReturn} Objeto con datos procesados y funciones de utilidad
 * 
 * @example
 * ```jsx
 * // Uso básico en componente
 * import useCustomerDetailLogic from 'hooks/inbox/forms/useCustomerDetailLogic';
 * 
 * const CustomerFieldsDisplay = ({ customerFields, customFields }) => {
 *   const { 
 *     customFieldsGrouped, 
 *     formatAnswer 
 *   } = useCustomerDetailLogic(customerFields, customFields);
 * 
 *   return (
 *     <div className="tw-wrapper">
 *       <h3>Campos Personalizados ({customFieldsGrouped.length})</h3>
 *       {customFieldsGrouped.map((field, index) => (
 *         <div key={field.id || index} className="tw-field-row">
 *           <label>{field.name}:</label>
 *           <span>{formatAnswer(field)}</span>
 *         </div>
 *       ))}
 *     </div>
 *   );
 * };
 * ```
 * 
 * @example
 * ```jsx
 * // Formateo especializado por tipo de campo
 * const CustomerFieldsTyped = ({ customerFields, customFields }) => {
 *   const { customFieldsGrouped, formatAnswer } = useCustomerDetailLogic(
 *     customerFields, 
 *     customFields
 *   );
 * 
 *   const booleanFields = customFieldsGrouped.filter(f => f.field_type === 'boolean');
 *   const dateFields = customFieldsGrouped.filter(f => f.field_type === 'date');
 * 
 *   return (
 *     <div className="tw-wrapper">
 *       <section>
 *         <h4>Campos Booleanos ({booleanFields.length})</h4>
 *         {booleanFields.map(field => (
 *           <div key={field.id}>{field.name}: {formatAnswer(field)}</div>
 *         ))}
 *       </section>
 *       
 *       <section>
 *         <h4>Campos de Fecha ({dateFields.length})</h4>
 *         {dateFields.map(field => (
 *           <div key={field.id}>{field.name}: {formatAnswer(field)}</div>
 *         ))}
 *       </section>
 *     </div>
 *   );
 * };
 * ```
 * 
 * @see {@link useCustomerDetailData} Para carga de datos de campos
 * @see {@link useCustomerDetailUI} Para estado de interfaz de usuario
 * @see {@link useCustomerDetailForm} Para manejo de formularios
 * @see {@link stringUtils} Utilidades de validación de strings
 * 
 * @since 1.0.0
 * @version 2.3.0 - Optimización completa, validación mejorada, tipado TypeScript-style
 */
function useCustomerDetailLogic(customerFields, customFields) {
  /**
   * Formatea el valor de respuesta de un campo personalizado según su tipo.
   * 
   * @function formatAnswer
   * @category Data Formatter
   * @description
   * Transforma el valor crudo guardado en la API al formato de visualización
   * apropiado para la interfaz de usuario, aplicando lógica específica por tipo.
   * 
   * **Transformaciones por tipo:**
   * - `boolean`: 't' → 'Sí', 'f' → 'No', '' → '-'
   * - `date`: string ISO → formato 'DD/MM/YYYY' validado
   * - `text`: string → string sin transformación
   * - `list`: key → label desde opciones disponibles
   * 
   * @param {ProcessedCustomField} field - Campo con información de tipo y valor
   * @param {*} field.answer - Valor a formatear
   * @param {'boolean'|'date'|'text'|'list'} field.field_type - Tipo del campo
   * @param {Array<ListOption>} [field.list_options] - Opciones para campos lista
   * 
   * @returns {string} Valor formateado para mostrar en UI
   * 
   * @example
   * ```javascript
   * // Boolean field
   * formatAnswer({ answer: 't', field_type: 'boolean' }); // → 'Sí'
   * formatAnswer({ answer: 'f', field_type: 'boolean' }); // → 'No'
   * formatAnswer({ answer: '', field_type: 'boolean' }); // → '-'
   * 
   * // Date field
   * formatAnswer({ answer: '2024-01-15', field_type: 'date' }); // → '15/01/2024'
   * formatAnswer({ answer: 'invalid', field_type: 'date' }); // → 'invalid'
   * 
   * // List field
   * formatAnswer({
   *   answer: 'option1',
   *   field_type: 'list',
   *   list_options: [{ value: 'option1', label: 'Opción 1' }]
   * }); // → 'Opción 1'
   * 
   * // Text field
   * formatAnswer({ answer: 'Texto libre', field_type: 'text' }); // → 'Texto libre'
   * ```
   * 
   * @performance O(n) donde n = cantidad de opciones en listas
   * @since 1.0.0
   * @version 2.1.0 - Mejorada validación de fechas
   */
  const formatAnswer = useCallback((field) => {
    if (!field) return '';

    switch (field.field_type) {
      case 'boolean':
        return field.answer ? 'Sí' : 'No';
        
      case 'date':
        if (field.answer && stringUtils.validateDateFormat(field.answer)) {
          return moment(field.answer).format("YYYY-MM-DD");
        }
        return '';
        
      default:
        return field.answer || '';
    }
  }, []);

  /**
   * Campos personalizados procesados con valores asignados desde customerFields.
   * 
   * @type {ProcessedCustomField[]}
   * @description
   * Array de definiciones de campos combinado con valores guardados del cliente.
   * Cada campo incluye su definición (nombre, tipo, requerido) más el valor
   * específico del cliente y metadatos de estado.
   * 
   * **Proceso de Combinación:**
   * 1. Crea copia profunda de customFields para evitar mutaciones
   * 2. Para cada custom field, busca valor correspondiente en customerFields
   * 3. Transforma valor según tipo (boolean 't'/'f', fechas, listas)
   * 4. Normaliza list_options de {key, value} a {value, label}
   * 5. Asigna id_saved para tracking de persistencia
   * 
   * **Optimización:** Usa useMemo con dependencias específicas para evitar
   * re-cálculos innecesarios cuando los datos no cambian.
   * 
   * @performance O(n*m) donde n = customFields, m = customerFields promedio
   * @memoized Depende de customerFields y customFields
   * @since 1.0.0
   */
  const customFieldsGrouped = useMemo(() => {
    if (!customFields || !Array.isArray(customFields)) {
      return [];
    }

    const clonedCustomFields = cloneDeep(customFields);
    
    forEach(clonedCustomFields, (field) => {
      // Inicializar valores por defecto
      field.answer = field.field_type === 'boolean' ? false : '';
      field.id_saved = null;
      
      // Buscar valor guardado para este campo
      const customerFieldIn = find(customerFields, { 
        customer_related_field_id: field.id 
      });

      if (customerFieldIn) {
        field.id_saved = customerFieldIn.id;
        
        // Procesar según tipo de campo
        switch (customerFieldIn.customer_related_field.field_type) {
          case 'boolean':
            field.answer = (customerFieldIn.data === 't');
            break;
            
          case 'date':
            field.answer = stringUtils.parseCustomerRelatedFieldDate(customerFieldIn.data);
            break;
            
          default:
            field.answer = customerFieldIn.data;
        }
      }

      // Formatear opciones de lista si es necesario
      if (field.field_type === 'list' && field.list_options) {
        field.list_options = field.list_options.map((option) => ({
          value: option.key,
          label: option.value
        }));
      }
    });

    return clonedCustomFields;
  }, [customFields, customerFields]);

  /**
   * Objeto retornado memoizado para evitar re-renders innecesarios de componentes.
   * 
   * @type {UseCustomerDetailLogicReturn}
   * @description
   * Retorna todas las funciones y datos procesados del hook en un objeto
   * memoizado que solo cambia cuando las dependencias realmente cambian,
   * optimizando el rendimiento de componentes que usan este hook.
   * 
   * **Datos incluidos:**
   * - `customFieldsGrouped`: Array de campos procesados con valores
   * 
   * **Funciones incluidas:**
   * - `formatAnswer`: Formateo de valores por tipo de campo
   * 
   * @performance Optimizado con useMemo para evitar re-creación de objeto
   * @memoized Depende de customFieldsGrouped y formatAnswer
   * @since 1.0.0
   */
  return useMemo(() => ({
    // Datos procesados
    customFieldsGrouped,
    
    // Funciones de utilidad
    formatAnswer
  }), [
    customFieldsGrouped,
    formatAnswer
  ]);
}

export default useCustomerDetailLogic;